import { useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import type {
  DrawLineStateType,
  DrawModeType,
  DrawingElement,
  SceneType,
  CameraAnimationConfig,
  EModelVersion,
  ProcedureSliceType,
} from 'features/Procedure/state/procedure.type'
import type { Vector3Like } from 'three'
import {
  updateSceneCoords,
  resetScene,
  setSelectedElement,
  setDrawMode,
  setDrawLineState,
  setDesiredCameraPosition,
  addDrawingElement,
  setDrawingElements,
  updateDrawingElement,
  toggleShowElementIds,
  setShowElementIds,
  deleteDrawingElement,
  setModelVersion,
  setIsPrinting,
  clearDrawingHistory,
  resetState,
  resetSceneSensitiveData,
  setModelSceneExpanded,
  setDrawCustomizerOpen,
} from 'features/Procedure/state/procedure.slice'

export const useSceneActions = () => {
  const dispatch = useAppDispatch()
  const drawMode = useAppSelector((state) => state.procedureReducer.drawMode)
  const isModelSceneExpanded = useAppSelector(
    (state) => state.procedureReducer.isModelSceneExpanded,
  )

  const handleUpdateSceneCoords = useCallback(
    (scene: SceneType) => {
      dispatch(updateSceneCoords(scene))
    },
    [dispatch],
  )

  const handleResetScene = useCallback(
    (payload?: { exceptions?: (keyof ProcedureSliceType)[] }) => {
      dispatch(resetScene(payload))
    },
    [dispatch],
  )

  const handleSetSelectedElement = useCallback(
    (element: DrawingElement | null, tooltipPosition?: Vector3Like) => {
      dispatch(setSelectedElement({ element, tooltipPosition }))
    },
    [dispatch],
  )

  const handleSetDrawMode = useCallback(
    (mode: DrawModeType) => {
      dispatch(setDrawMode(mode))
    },
    [dispatch],
  )

  const handleSetDrawLineState = useCallback(
    (state: DrawLineStateType) => {
      dispatch(setDrawLineState(state))
    },
    [dispatch],
  )

  const handleSetDesiredCameraPosition = useCallback(
    (config: CameraAnimationConfig | null) => {
      dispatch(setDesiredCameraPosition(config))
    },
    [dispatch],
  )

  const handleAddDrawingElement = useCallback(
    (element: Omit<DrawingElement, 'visibleState' | 'timestamp'>) => {
      dispatch(addDrawingElement(element))
    },
    [dispatch],
  )

  const handleSetDrawingElements = useCallback(
    (elements: DrawingElement[]) => {
      dispatch(setDrawingElements(elements))
    },
    [dispatch],
  )

  const handleUpdateDrawingElement = useCallback(
    (id: string, updates: Partial<DrawingElement>) => {
      dispatch(updateDrawingElement({ id, updates }))
    },
    [dispatch],
  )

  const handleToggleDrawMode = useCallback(
    (mode: DrawModeType) => {
      dispatch(setDrawMode(drawMode === mode ? null : mode))
    },
    [dispatch, drawMode],
  )

  const handleToggleShowElementIds = useCallback(() => {
    dispatch(toggleShowElementIds())
  }, [dispatch])

  const handleSetShowElementIds = useCallback(
    (show: boolean) => {
      dispatch(setShowElementIds(show))
    },
    [dispatch],
  )

  const handleDeleteDrawingElement = useCallback(
    (id: string) => {
      dispatch(deleteDrawingElement(id))
    },
    [dispatch],
  )
  const handleSetModelVersion = useCallback(
    (version: EModelVersion) => {
      dispatch(setModelVersion(version))
    },
    [dispatch],
  )
  const handleSetIsPrinting = useCallback(
    (isPrinting: boolean) => {
      dispatch(setIsPrinting(isPrinting))
    },
    [dispatch],
  )
  const handleClearDrawingHistory = useCallback(() => {
    dispatch(clearDrawingHistory())
  }, [dispatch])
  const handleResetState = useCallback(() => {
    dispatch(resetState())
  }, [dispatch])

  const handleResetSceneSensitiveData = useCallback(() => {
    dispatch(resetSceneSensitiveData())
  }, [dispatch])

  const handleToggleModelSceneExpanded = useCallback(() => {
    dispatch(setModelSceneExpanded(!isModelSceneExpanded))
  }, [dispatch, isModelSceneExpanded])

  const handleSetModelSceneExpanded = useCallback(
    (expanded: boolean) => {
      dispatch(setModelSceneExpanded(expanded))
    },
    [dispatch],
  )

  const handleSetDrawCustomizerOpen = useCallback(
    (isOpen: boolean) => {
      dispatch(setDrawCustomizerOpen(isOpen))
    },
    [dispatch],
  )

  return useMemo(
    () => ({
      handleUpdateSceneCoords,
      handleResetScene,
      handleSetSelectedElement,
      handleSetDrawMode,
      handleSetDrawLineState,
      handleSetDesiredCameraPosition,
      handleAddDrawingElement,
      handleSetDrawingElements,
      handleUpdateDrawingElement,
      handleToggleDrawMode,
      handleToggleShowElementIds,
      handleSetShowElementIds,
      handleDeleteDrawingElement,
      handleSetModelVersion,
      handleSetIsPrinting,
      handleClearDrawingHistory,
      handleResetState,
      handleResetSceneSensitiveData,
      handleToggleModelSceneExpanded,
      handleSetModelSceneExpanded,
      handleSetDrawCustomizerOpen,
    }),
    [
      handleUpdateSceneCoords,
      handleResetScene,
      handleSetSelectedElement,
      handleSetDrawMode,
      handleSetDrawLineState,
      handleSetDesiredCameraPosition,
      handleAddDrawingElement,
      handleSetDrawingElements,
      handleUpdateDrawingElement,
      handleToggleDrawMode,
      handleToggleShowElementIds,
      handleSetShowElementIds,
      handleDeleteDrawingElement,
      handleSetModelVersion,
      handleSetIsPrinting,
      handleClearDrawingHistory,
      handleResetState,
      handleResetSceneSensitiveData,
      handleToggleModelSceneExpanded,
      handleSetModelSceneExpanded,
      handleSetDrawCustomizerOpen,
    ],
  )
}
